// activityTabsViewModel.js — m3-11 Activity tab strip view model (pure, node-testable)
//
// The Activity destination has two tabs: Project Board (id 'board') ⇄ Triggered Tasks
// (id 'agents', label "Triggered Tasks"). This module owns the tab definitions + the
// host-controlled selection contract, so the segmented control (page: sticky/blurred;
// panel: plain) and the gate agree on one source.
//
// HOST-CONTROLLED: the tab strip never owns the active tab. The shell (m3-06a) holds
// `activityView` (lifted so it survives Standard⇄Agent and mirrors site↔panel) and passes
// it down. This module formalizes that: `buildTabStrip(active, placement)` returns the
// render descriptor; an invalid `active` falls back to the default (never crashes).

export const ACTIVITY_TABS = Object.freeze([
  { id: "board", label: "Project Board", icon: "activity" },
  { id: "agents", label: "Agent Jobs", icon: "robot" },
])
export const DEFAULT_TAB = "board"

export function isValidTab(id) {
  return ACTIVITY_TABS.some((t) => t.id === id)
}

// Build the tab-strip descriptor. `active` is host-controlled; `placement` is 'page'|'panel'.
// Page placement gets the sticky/blurred segmented control; panel gets a plain inline one.
export function buildTabStrip(active, placement = "page") {
  const resolvedActive = isValidTab(active) ? active : DEFAULT_TAB
  const inPanel = placement === "panel"
  return {
    active: resolvedActive,
    placement,
    sticky: !inPanel,   // .act-tabs sticky+blur only when full-page
    blurred: !inPanel,
    tabs: ACTIVITY_TABS.map((t) => ({
      ...t,
      selected: t.id === resolvedActive,
    })),
  }
}

// The next active tab when a tab is clicked — pure, so the gate can assert the host's
// selection logic without React. A click on an invalid id is a no-op (returns current).
export function selectTab(current, clickedId) {
  if (!isValidTab(clickedId)) return isValidTab(current) ? current : DEFAULT_TAB
  return clickedId
}
